// Calendar windows on the RFQ DATE axis, for the sidebar's "Período (data do
// RFQ)" control and the KPI benchmark beside it.
//
// This is a different axis from the tenor slider, which windows by time to
// MATURITY (`tenorDays`). The two stack: an RFQ quoted last week maturing in 90
// days is inside both. Don't merge them.
//
// Everything here is anchored on the DATASET's most recent RFQ, never on
// `new Date()`. Every export under data/csv_flow_sight/ is historical (the
// newest row is months behind the real clock), so a wall-clock anchor would
// empty every window.

export const RFQ_PERIODS = ['ALL', 'DAY', 'WEEK', 'FORTNIGHT', 'MONTH']
export const BENCHMARKS = ['NONE', 'DAY', 'WEEK', 'FORTNIGHT', 'MONTH']

// Rows that came back from localStorage went through JSON, so rfqtimestamp is
// an ISO string there and a Date everywhere else. Both must work.
export function toDate (value) {
  if (!value) return null
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate())
const endOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999)
// Preserves the time of day. Shifting a window means moving BOTH ends by the
// same amount, and the upper end is an end-of-day (23:59:59.999) - rebuilding
// it from y/m/d alone would snap it back to midnight and quietly cut the last
// day out of every benchmark window.
const addDays = (d, n) => {
  const out = new Date(d.getTime())
  out.setDate(out.getDate() + n)
  return out
}
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1)
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999)

// Most recent RFQ in the dataset - the anchor every window below hangs off.
export function datasetAnchor (rows = []) {
  let anchor = null
  for (const row of rows) {
    const date = toDate(row?.rfqtimestamp)
    if (date && (!anchor || date > anchor)) anchor = date
  }
  return anchor
}

// null means "no date filter at all" (period ALL, or no dataset loaded).
export function periodWindow (period, anchor) {
  if (!anchor || period === 'ALL' || !RFQ_PERIODS.includes(period)) return null
  if (period === 'DAY') return { from: startOfDay(anchor), to: endOfDay(anchor) }
  // Rolling 7 days ending on the anchor, not the ISO week: a dataset whose last
  // day is a Tuesday would otherwise report a two-day "week".
  if (period === 'WEEK') return { from: startOfDay(addDays(anchor, -6)), to: endOfDay(anchor) }
  // Same rolling logic, 14 days.
  if (period === 'FORTNIGHT') return { from: startOfDay(addDays(anchor, -13)), to: endOfDay(anchor) }
  return { from: startOfMonth(anchor), to: endOfMonth(anchor) }
}

// A benchmark only makes sense against its OWN granularity: comparing a whole
// month against a single day is arithmetic, not information. The benchmark must
// therefore match the period, and anything else resolves to null - the sidebar
// enforces the same rule by only enabling the matching button.
export function benchmarkMatchesPeriod (period, benchmark) {
  return benchmark !== 'NONE' && BENCHMARKS.includes(benchmark) && benchmark === period
}

// The current window stepped back by exactly one period.
export function benchmarkWindow (period, benchmark, anchor) {
  const current = periodWindow(period, anchor)
  // periodWindow is null for ALL, which is also the case with nothing to step
  // back from - the dataset has no "previous everything".
  if (!current || !benchmarkMatchesPeriod(period, benchmark)) return null

  const { from, to } = current
  if (benchmark === 'DAY') return { from: addDays(from, -1), to: addDays(to, -1) }
  if (benchmark === 'WEEK') return { from: addDays(from, -7), to: addDays(to, -7) }
  if (benchmark === 'FORTNIGHT') return { from: addDays(from, -14), to: addDays(to, -14) }

  // Calendar month, so the previous one is rebuilt from its own length rather
  // than shifted - stepping both ends back 31 days would miss February, and
  // shifting a June 30 end by one month would drop May 31.
  const previous = startOfMonth(new Date(from.getFullYear(), from.getMonth() - 1, 1))
  return { from: previous, to: endOfMonth(previous) }
}

export function isWithin (date, window) {
  if (!window) return true
  const value = toDate(date)
  if (!value) return false
  return value >= window.from && value <= window.to
}

// Signed relative change, or null when no percentage can honestly be stated.
//
// Two different things produce a null and the UI must not conflate them:
// a comparison window with no rows at all (caller passes a null baseline), and
// a window that HAS rows whose metric happens to be zero - a day with 64 RFQs
// and no DEAL has a real, measured 0% conversion. Going from 0 to anything is
// an undefined percentage but a genuine improvement, so the caller still
// colours it by direction; only 0 to 0 is truly no change.
export function variationPct (current, baseline) {
  if (baseline === null || baseline === undefined || !Number.isFinite(baseline)) return null
  if (!Number.isFinite(current)) return null
  if (baseline === 0) return current === 0 ? 0 : null
  return ((current - baseline) / Math.abs(baseline)) * 100
}
